import endOfMonth from 'date-fns/endOfMonth'
import startOfMonth from 'date-fns/startOfMonth'
import startOfYear from 'date-fns/startOfYear'
import subMonths from 'date-fns/subMonths'
import isLastDayOfMonth from 'date-fns/isLastDayOfMonth'
import formatISO from 'date-fns/formatISO'
import TysiaButton from 'DesignSystem/TysiaButton'
import TysiaButtonIcon from 'DesignSystem/TysiaButton/TysiaButtonIcon'
import TysiaButtonText from 'DesignSystem/TysiaButton/TysiaButtonText/index.js'
import TysiaIconPerson from 'DesignSystem/Svgs/TysiaIconPerson'
import TysiaTag from 'DesignSystem/TysiaTag/index.js'
import TysiaTagText from 'DesignSystem/TysiaTag/TysiaTagText/index.js'
import { useDataSubmit } from 'Simple/Data.js'

export let REPORT_TYPES = /** @type {const} */ ({
  ACCOUNTS_RECEIVABLE: 'accounts_receivable',
  ADJUSTMENTS: 'adjustments',
  CASH_FLOW: 'cash_flow',
  COLLECTIONS: 'collections',
  FINANCIAL: 'financial',
  PATIENT_REFERRALS: 'patient_referrals',
  PRACTICE_EFFICIENCY: 'practice_efficiency',
  PRACTICE_MONITOR: 'practice_monitor',
  PRODUCTION_BY_TRANSACTION_DATE: 'production_by_transaction_date',
  TOKEN_MIGRATION: 'token_migration',
  TRANSACTIONS: 'transactions',
  UNAPPLIED_PAYMENTS: 'unapplied_payments',
  STATEMENTS: 'statements',
  MY_REPORTS: 'my_reports',
})

function generateNumberValueGetterFunc(
  params,
  divisorKey,
  dividendKey,
  isPercentage = true
) {
  let multiply = isPercentage ? 100 : 1

  if (!params.node.group) {
    // no need to handle group levels - calculated in the 'generatePercentageAggrFunc'
    let divisor = params.data[divisorKey]
    let dividend = params.data[dividendKey]
    return {
      divisor,
      dividend,
      toString: () =>
        divisor && dividend
          ? ((dividend / divisor) * multiply).toString()
          : '0',
      toNumber: () =>
        divisor && dividend ? (dividend / divisor) * multiply : 0,
    }
  }
}

function generateNumberAggrFunc(params, isPercentage = true) {
  let multiply = isPercentage ? 100 : 1
  let { total_divisor, total_dividend } = params.values.reduce(
    (acc, item) => {
      if (item.divisor) {
        acc.total_divisor += item.divisor
      }
      if (item.dividend) {
        acc.total_dividend += item.dividend
      }

      return acc
    },
    {
      total_divisor: 0,
      total_dividend: 0,
    }
  )

  if (!total_divisor || !total_dividend) {
    return 0
  }

  return total_divisor && total_dividend
    ? (total_dividend / total_divisor) * multiply
    : 0
}

/**
 * @param {string[]} times Array of strings "HH:MM:SS"
 * @returns {string} Average as "HH:MM:SS"
 */
function averageTime(times) {
  // 1) sum total seconds
  let totalSeconds = times
    .filter(item => ![undefined, null, ''].includes(item?.trim()))
    .reduce((sum, hhmmss) => {
      let [h, m, s] = hhmmss.split(':').map(Number)
      return sum + h * 3600 + m * 60 + s
    }, 0)

  // 2) compute average (in seconds)
  let avg = totalSeconds / times.length

  // 3) convert back to H, M, S
  let hours = Math.floor(avg / 3600)
  let minutes = Math.floor((avg % 3600) / 60)
  let seconds = Math.round(avg % 60)

  // pad to 2 digits each
  let pad = n => String(n).padStart(2, '0')
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

export let REPORT_CONFIGURATIONS = /** @type {const} */ ({
  [REPORT_TYPES.ACCOUNTS_RECEIVABLE]: ({
    last_execution,
    current_location,
    viewPath,
  }) => {
    return {
      type: REPORT_TYPES.ACCOUNTS_RECEIVABLE,
      title: 'Accounts receivable',
      accounts_receivable_filters: {
        due_buckets: [
          'DUE_0_30',
          'DUE_31_60',
          'DUE_61_90',
          'DUE_91_120',
          'DUE_MORE_120_DAYS',
        ],
        include_future_due: false,
        include_credits: false,
        account_types: ['Insurance Payer', 'Medicaid', 'Private Payer'],
      },
      beta: true,
      columns: [
        {
          id: 'LOCATION',
          type: 'STRING',
          text: 'Location',
        },
        {
          id: 'PATIENT',
          type: 'STRING',
          text: 'Patient ID',
        },
        {
          id: 'PATIENT_NAME',
          type: 'PATIENT_LINK',
          text: 'Patient',
          cellRenderer: params => (
            <PatientCellRenderer
              viewPath={viewPath}
              data={params.data}
              value={params.value}
              valueFormatted={params.valueFormatted}
            />
          ),
        },
        {
          id: 'PATIENT_LEGACY_ID',
          type: 'STRING',
          text: 'Legacy ID',
        },
        {
          id: 'ACCOUNT_TYPE',
          type: 'STRING',
          text: 'Payor Type',
        },
        {
          id: 'PAYER_NAME',
          type: 'STRING',
          text: 'Payor',
        },
        {
          id: 'INSURANCE_MEMBER_ID',
          type: 'STRING',
          text: 'Member ID',
        },
        {
          id: 'SUBSCRIBER_NAME',
          type: 'TEXT',
          text: 'Subscriber',
        },
        {
          id: 'PHONES',
          type: 'PHONE',
          text: 'Phone',
        },
        {
          id: 'EMAILS',
          type: 'STRING',
          text: 'Email',
        },
        {
          id: 'NEXT_APPOINTMENT',
          type: 'DATE',
          text: 'Next Appt',
        },
        {
          id: 'DUE_0_30',
          type: 'MONEY',
          text: '0-30 Days',
        },
        {
          id: 'DUE_31_60',
          type: 'MONEY',
          text: '31-60 Days',
        },
        {
          id: 'DUE_61_90',
          type: 'MONEY',
          text: '61-90 Days',
        },
        {
          id: 'DUE_91_120',
          type: 'MONEY',
          text: '91-120 Days',
        },
        {
          id: 'DUE_MORE_120_DAYS',
          type: 'MONEY',
          text: '120+ Days',
        },
        {
          id: 'DUE_NOW',
          type: 'MONEY',
          text: 'Due Now',
        },
        {
          id: 'FUTURE_DUE',
          type: 'MONEY',
          text: 'Future Due',
        },
        {
          id: 'ACCOUNT_RECEIVABLES',
          type: 'MONEY',
          text: 'Total AR',
        },
        {
          id: 'UNAPPLIED_CREDIT',
          type: 'MONEY',
          text: 'Unapplied Credit',
        },
        {
          id: 'OLDEST_DUE_DATE',
          type: 'DATE',
          text: 'Oldest Due',
        },
        {
          id: 'LAST_PAYMENT_DATE',
          type: 'DATE',
          text: 'Last Payment Date',
        },
        {
          id: 'LAST_PAYMENT_AMOUNT',
          type: 'MONEY',
          text: 'Last Payment',
        },
        {
          id: 'LABEL',
          type: 'STRING',
          text: 'Label',
        },
        {
          id: 'LABEL_AGENCY',
          type: 'STRING',
          text: 'Label Agency',
        },
        {
          id: 'LAST_UPDATED_BY',
          type: 'STRING',
          text: 'Last Updated By',
        },
        {
          id: 'LAST_UPDATED',
          type: 'DATE_TIME',
          text: 'Last Updated',
        },
      ],
      filter_values: {
        location_ids: [current_location.id],
        reference_date: new Date(),
        ...last_execution.filters,
      },
      filters: [
        {
          id: 'REFERENCE_DATE',
          column_name: 'Reference Date',
          type: 'DATE',
          required: true,
        },
        {
          id: 'LOCATION',
          column_name: 'Location',
          type: 'LOCATION',
          required: true,
        },
      ],
      last_execution,
      menu: true,
    }
  },
  [REPORT_TYPES.ADJUSTMENTS]: ({
    last_execution,
    current_location,
    viewPath,
  }) => {
    return {
      type: REPORT_TYPES.ADJUSTMENTS,
      title: 'Adjustments',
      beta: false,
      columns: [
        {
          id: 'ADJUSTMENT_DATETIME',
          type: 'DATE_TIME',
          text: 'Date & Time',
        },
        {
          id: 'LOCATION',
          type: 'STRING',
          text: 'Location',
        },
        {
          id: 'ID',
          type: 'HUMAN_ID',
          text: 'Patient ID',
        },
        {
          id: 'FULL_NAME',
          type: 'STRING',
          text: 'Patient Full Name',
        },
        {
          id: 'RECEIVABLE_ID',
          type: 'STRING',
          text: 'Invoice',
        },
        {
          id: 'PAYMENT_ID',
          type: 'STRING',
          text: 'Payment',
        },
        {
          id: 'ADJUSTMENT_TYPE',
          type: 'ADJUSTMENT_TYPE',
          text: 'Adjustment Type',
        },
        {
          id: 'ADJUSTMENT_DESCRIPTION',
          type: 'TEXT',
          text: 'Description',
        },
        {
          id: 'ADJUSTMENT_USER',
          type: 'STRING',
          text: 'User who made the adjustment',
        },
        {
          id: 'ADJUSTMENT_AMOUNT',
          type: 'MONEY',
          text: 'Amount',
        },
      ],
      filter_values: {
        location_ids: [current_location.id],
        start_date: startOfMonth(new Date()),
        end_date: new Date(),
        adjustment_type_ids: [
          'FixAdjustment',
          'DiscountAdjustment',
          'DiscountReverseAdjustment',
          'WriteOff',
          'REFUND',
          'PAYMENT_CORRECTION',
        ],
        ...last_execution.filter_values,
      },
      filters: [
        {
          id: 'ADJUSTMENT_DATETIME',
          column_name: 'Date & Time',
          type: 'DATE_RANGE',
        },
        {
          id: 'ADJUSTMENT_TYPE',
          column_name: 'Adjustment Type',
          type: 'ADJUSTMENT_TYPE',
        },
        {
          id: 'LOCATION',
          column_name: 'Location',
          type: 'LOCATION',
        },
      ],
      last_execution,
      menu: true,
    }
  },
  [REPORT_TYPES.CASH_FLOW]: ({
    last_execution,
    current_location,
    viewPath,
  }) => {
    return {
      type: REPORT_TYPES.CASH_FLOW,
      title: 'Cash Flow',
      beta: false,
      columns: [
        {
          id: 'LOCATION',
          type: 'STRING',
          text: 'Location',
          rowGroup: true,
          hide: true,
          context: {
            hide: false,
          },
        },
        {
          id: 'ACCOUNT_TYPE',
          type: 'STRING',
          text: 'Account Type',
          rowGroup: true,
          hide: true,
        },
        {
          id: 'DUE_DATE',
          type: 'DATE',
          text: 'Due Date',
          rowGroup: true,
          hide: true,
        },
        { id: 'INVOICE_ID', type: 'HUMAN_ID', text: 'Invoice' },
        {
          id: 'PATIENT_FULL_NAME',
          type: 'PATIENT_LINK',
          text: 'Patient',
          cellRenderer: params => (
            <PatientCellRenderer
              viewPath={viewPath}
              data={params.data}
              value={params.value}
              valueFormatted={params.valueFormatted}
            />
          ),
        },
        { id: 'PATIENT_HUMAN_ID', type: 'HUMAN_ID', text: 'Patient Id' },
        { id: 'PAYOR', type: 'STRING', text: 'Payor' },
        {
          id: 'EXPECTED',
          type: 'MONEY',
          text: 'Expected',
        },
        {
          id: 'COLLECTED',
          type: 'MONEY',
          text: 'Collected',
        },
        {
          id: 'REMAINING',
          type: 'MONEY',
          text: 'To be collected',
        },
      ],
      filter_values: {
        location_ids: [current_location.id],
        start_date: subMonths(new Date(), 1),
        end_date: new Date(),
        financial_payor_types: ['Private Payer', 'Insurance Payer', 'Medicaid'],
        ...last_execution.filter_values,
      },
      filters: [
        {
          id: 'START_DATE',
          column_name: 'Transaction Date',
          type: 'DATE_RANGE',
          required: true,
        },
        {
          id: 'LOCATION',
          column_name: 'Location',
          type: 'LOCATION',
          required: true,
        },
        {
          id: 'FINANCIAL_PAYOR_TYPE',
          column_name: 'Type',
          type: 'FINANCIAL_PAYOR_TYPE',
          required: true,
        },
      ],
      grid_options: {
        showOpenedGroup: true,
        groupDefaultExpanded: -1,
        suppressAggFuncInHeader: true,
        groupIncludeFooter: false,
      },
      last_execution,
      menu: true,
    }
  },
  [REPORT_TYPES.COLLECTIONS]: ({
    last_execution,
    current_location,
    viewPath,
  }) => {
    return {
      type: REPORT_TYPES.COLLECTIONS,
      title: 'Collections',
      beta: false,
      columns: [
        {
          id: 'LOCATION',
          type: 'STRING',
          text: 'Location',
          context: {
            hide: false,
          },
        },
        {
          id: 'PAYOR',
          type: 'STRING',
          text: 'Payor',
          context: {
            hide: false,
          },
        },
        { id: 'NET_COLLECTIONS', type: 'MONEY', text: 'Net Collections' },
      ],
      filter_values: {
        location_ids: [current_location.id],
        start_date: startOfMonth(new Date()),
        end_date: new Date(),
        financial_payor_types: ['Private Payer', 'Insurance Payer'],
        ...last_execution.filters,
      },
      filters: [
        {
          id: 'FINANCIAL_PAYOR_TYPE',
          column_name: 'Payor',
          type: 'FINANCIAL_PAYOR_TYPE',
          required: true,
        },
        {
          id: 'START_DATE',
          column_name: 'Date',
          type: 'DATE_RANGE',
          required: true,
        },
        {
          id: 'LOCATION',
          column_name: 'Location',
          type: 'LOCATION',
          required: true,
        },
      ],
      last_execution,
      menu: true,
      report_type_values: [],
      report_types: [],
    }
  },
  [REPORT_TYPES.FINANCIAL]: ({
    last_execution,
    current_location,
    viewPath,
  }) => {
    return {
      type: REPORT_TYPES.FINANCIAL,
      title: 'Financial Summary',
      beta: false,
      chart: {
        title: 'Financial Summary',
        data: [],
        series: [],
        axes: [],
      },
      columns: [
        {
          id: 'MONTH',
          type: 'STRING',
          text: 'Month',
          context: {
            hide: false,
          },
        },
        {
          id: '{year}',
          type: 'GROUP',
          text: '{year}',
          children: [
            {
              id: '{year}_PRODUCTION',
              type: 'GROUP',
              text: 'Production',
              children: [
                {
                  id: '{year}_GROSS_PRODUCTION',
                  type: 'MONEY',
                  text: 'Gross',
                },
                {
                  id: '{year}_ADJUSTMENTS_PRODUCTION',
                  type: 'MONEY',
                  text: 'Adjustments',
                },
                {
                  id: '{year}_NET_PRODUCTION',
                  type: 'MONEY',
                  text: 'Net',
                  context: {
                    hide: false,
                  },
                },
              ],
            },
            {
              id: '{year}_COLLECTION',
              type: 'GROUP',
              text: 'Collection',
              children: [
                {
                  id: '{year}_GROSS_COLLECTION',
                  type: 'MONEY',
                  text: 'Gross',
                },
                {
                  id: '{year}_ADJUSTMENTS_COLLECTION',
                  type: 'MONEY',
                  text: 'Adjustments',
                },
                {
                  id: '{year}_NET_COLLECTION',
                  type: 'MONEY',
                  text: 'Net',
                  context: {
                    hide: false,
                  },
                },
              ],
            },
          ],
        },
      ],
      filter_values: {
        location_ids: [current_location.id],
        reference_date: startOfYear(new Date()),
        financial_payor_types: ['Private Payer', 'Insurance Payer'],
        ...last_execution.filters,
      },
      filters: [
        {
          id: 'FINANCIAL_PAYOR_TYPE',
          column_name: 'Payor',
          type: 'FINANCIAL_PAYOR_TYPE',
          required: true,
        },
        {
          id: 'REFERENCE_DATE',
          column_name: 'Date',
          type: 'DATE',
          required: true,
        },
        {
          id: 'LOCATION',
          column_name: 'Location',
          type: 'LOCATION',
          required: true,
        },
      ],
      last_execution,
      menu: true,
      onToggleColumnsVisibility: null,
      report_type_values: [
        'ADJUSTMENTS',
        '/GROSS_COLLECTION$/',
        '/ADJUSTMENT_COLLECTION$/',
        '/GROSS_PRODUCTION$/',
        '/ADJUSTMENT_PRODUCTION$/',
      ],
      report_types: [
        {
          id: 'ADJUSTMENTS',
          text: 'Show Details',
          children: [
            '/GROSS_COLLECTION$/',
            '/ADJUSTMENT_COLLECTION$/',
            '/GROSS_PRODUCTION$/',
            '/ADJUSTMENT_PRODUCTION$/',
          ],
        },
      ],
      showDetails: true,
    }
  },
  [REPORT_TYPES.PATIENT_REFERRALS]: ({
    last_execution,
    current_location,
    viewPath,
  }) => ({
    type: REPORT_TYPES.PATIENT_REFERRALS,
    title: 'Patient Referrals',
    beta: true,
    columns: [
      {
        id: 'LOCATION',
        type: 'STRING',
        text: 'Location',
      },
      {
        id: 'PATIENT',
        type: 'STRING',
        text: 'Patient ID',
      },
      {
        id: 'PATIENT_NAME',
        type: 'PATIENT_LINK',
        text: 'Patient',
        cellRenderer: params => (
          <PatientCellRenderer
            viewPath={viewPath}
            data={params.data}
            value={params.value}
            valueFormatted={params.valueFormatted}
            tabName="profile"
          />
        ),
      },
      {
        id: 'CREATED_DATE',
        type: 'DATE',
        text: 'Created date',
      },
      {
        id: 'TREATMENT_STATUS',
        type: 'STRING',
        text: 'Treatment Status',
      },
      {
        id: 'REFERRAL_TYPE',
        type: 'STRING',
        text: 'Referral Type',
        rowGroup: true,
        hide: true,
      },
      {
        id: 'REFERRAL_NAME',
        type: 'STRING',
        text: 'Referral',
        rowGroup: true,
        hide: true,
      },
      {
        id: 'GROSS_PRODUCTION',
        type: 'MONEY',
        text: 'Gross Production',
      },
      {
        id: 'DISCOUNTS_WRITE_OFF',
        type: 'MONEY',
        text: "Discounts & Write-off's",
      },
      {
        id: 'NET_PRODUCTION',
        type: 'MONEY',
        text: 'Net Production',
      },
      {
        id: 'NO_SHOW_CANCELLATION_APPOINTMENT_TOTAL',
        type: 'NUMBER',
        text: 'No Show/Cancellation appointment total',
      },
      {
        id: 'LAST_COMPLETED_APPOINTMENT_TEMPLATE',
        type: 'STRING',
        text: 'Last Completed Appointment Template',
      },
    ],
    filter_values: {
      location_ids: [current_location.id],
      start_date: startOfMonth(new Date()),
      end_date: new Date(),
      ...last_execution.filters,
    },
    filters: [
      {
        id: 'CREATED_DATE',
        column_name: 'Created date',
        type: 'DATE_RANGE',
        required: true,
      },
      {
        id: 'LOCATION',
        column_name: 'Location',
        type: 'LOCATION',
        required: true,
      },
    ],
    grid_options: {
      showOpenedGroup: true,
      groupDefaultExpanded: 2,
      suppressAggFuncInHeader: true,
      groupIncludeFooter: false,
    },
    last_execution,
    menu: true,
  }),
  [REPORT_TYPES.PRACTICE_EFFICIENCY]: ({
    last_execution,
    current_location,
    viewPath,
  }) => {
    return {
      type: REPORT_TYPES.PRACTICE_EFFICIENCY,
      title: 'Practice Efficiency',
      beta: false,
      columns: [
        {
          id: 'LOCATION',
          type: 'STRING',
          text: 'Location',
          context: {
            hide: false,
          },
        },
        {
          id: 'PATIENT_NAME',
          type: 'STRING',
          text: 'Patient name',
          context: {
            hide: false,
          },
        },
        {
          id: 'COMPLETED_APPOINTMENT',
          type: 'STRING',
          text: 'Completed appointment',
        },
        { id: 'APPT_TYPE_LENGTH', type: 'STRING', text: 'Appt type (length)' },
        { id: 'IS_EMERGENCY', type: 'BOOLEAN', text: 'Is emergency?' },
        { id: 'ASSISTANT', type: 'STRING', text: 'Assistant' },
        {
          id: 'APPT_DATE',
          type: 'DATE',
          text: 'Date',
        },
        {
          id: 'PATIENT_CHECK_IN',
          type: 'DATE',
          text: 'Check in time',
        },
        {
          id: 'PATIENT_CHECK_OUT',
          type: 'DATE',
          text: 'Checked out time',
        },
        {
          id: 'TOTAL_LENGTH',
          type: 'DATE',
          text: 'Actual length',
          aggFunc: params => averageTime(params.values),
        },
        {
          id: 'WAITING_TIME',
          type: 'DATE',
          text: 'Waiting time',
          aggFunc: params => averageTime(params.values),
        },
        {
          id: 'SEATING_TIME',
          type: 'DATE',
          text: 'Chair time',
          aggFunc: params => averageTime(params.values),
        },
        {
          id: 'WAITING_ON_DOCTOR',
          type: 'DATE',
          text: 'Waiting on doctor',
          aggFunc: params => averageTime(params.values),
        },
        {
          id: 'WITH_DOCTOR',
          type: 'DATE',
          text: 'With doctor',
          aggFunc: params => averageTime(params.values),
        },
        {
          id: 'CHECKING_OUT_TIME',
          type: 'DATE',
          text: 'Post chair time',
          aggFunc: params => averageTime(params.values),
        },
      ],
      filter_values: {
        location_ids: [current_location.id],
        start_date: startOfMonth(new Date()),
        end_date: new Date(),
        ...last_execution.filters,
      },
      filters: [
        {
          id: 'START_DATE',
          column_name: 'Completed Appointment',
          type: 'DATE_RANGE',
          required: true,
        },
        {
          id: 'LOCATION',
          column_name: 'Location',
          type: 'LOCATION',
          required: true,
        },
      ],
      last_execution,
      menu: true,
      report_type_values: [],
      report_types: [],
    }
  },
  [REPORT_TYPES.PRACTICE_MONITOR]: ({
    last_execution,
    current_location,
    viewPath,
  }) => {
    return {
      type: REPORT_TYPES.PRACTICE_MONITOR,
      title: 'Practice Monitor',
      beta: false,
      columns: [
        {
          id: 'LOCATION',
          type: 'STRING',
          text: 'Location',
          context: {
            hide: false,
          },
        },
        {
          id: 'PRODUCTION',
          type: 'GROUP',
          text: 'Production',
          children: [
            {
              id: 'GROSS_PRODUCTION',
              type: 'MONEY',
              text: 'Gross Production (Prod)',
            },
            {
              id: 'GROSS_RECEIPTS',
              type: 'MONEY',
              text: 'Gross Collections',
            },
            {
              id: 'REC_PERCENT',
              type: 'PERCENTAGE',
              text: 'Coll to Prod Ratio (%)',
              valueGetter: params =>
                generateNumberValueGetterFunc(
                  params,
                  'GROSS_PRODUCTION',
                  'GROSS_RECEIPTS'
                ),
              aggFunc: params => generateNumberAggrFunc(params),
            },
            {
              id: 'NET_PRODUCTION_ADJUSTMENTS',
              type: 'MONEY',
              text: 'Net Prod Adj',
            },
            {
              id: 'PROD_ADJ_PERCENT',
              type: 'PERCENTAGE',
              text: 'Prod Adj Ratio (%)',
              valueGetter: params =>
                generateNumberValueGetterFunc(
                  params,
                  'GROSS_PRODUCTION',
                  'NET_PRODUCTION_ADJUSTMENTS'
                ),
              aggFunc: params => generateNumberAggrFunc(params),
            },
            {
              id: 'NET_RECEIPTS_ADJUSTMENTS',
              type: 'MONEY',
              text: 'Net Coll Adj',
            },
            {
              id: 'REC_ADJ_PERCENT',
              type: 'PERCENTAGE',
              text: 'Coll Adj Ratio (%)',
            },
            {
              id: 'NET_PRODUCTION',
              type: 'MONEY',
              text: 'Net Prod',
            },
            {
              id: 'NET_COLLECTION',
              type: 'MONEY',
              text: 'Net Coll',
            },
            { id: 'OTHER_CHARGES', type: 'MONEY', text: 'Non Prod Charges' },
            { id: 'OTHER_DISCOUNTS', type: 'MONEY', text: 'Non Prod Disc' },
          ],
        },
        {
          id: 'AR',
          type: 'GROUP',
          text: 'A/R',
          children: [
            {
              id: 'ACCOUNTS_RECEIVABLE',
              type: 'MONEY',
              text: 'Accounts receivable (AR)',
            },
            {
              id: 'NET_AR_RATIO',
              type: 'PERCENTAGE',
              text: 'AR to Net Prod Ratio (%)',
              valueGetter: params => {
                if (!params.node.group) {
                  // no need to handle group levels - calculated in the 'generatePercentageAggrFunc'
                  let divisor = params.data.NET_PRODUCTION
                  let dividend = params.data.ACCOUNTS_RECEIVABLE
                  return {
                    divisor,
                    dividend,
                    toString: () =>
                      divisor && dividend
                        ? (dividend / divisor).toString()
                        : '0',
                    toNumber: () =>
                      divisor && dividend ? dividend / divisor : 0,
                  }
                }
              },
              aggFunc: params => {
                let { total_divisor, total_dividend } = params.values.reduce(
                  (acc, item) => {
                    if (item.divisor) {
                      acc.total_divisor += item.divisor
                    }
                    if (item.dividend) {
                      acc.total_dividend += item.dividend
                    }

                    return acc
                  },
                  {
                    total_divisor: 0,
                    total_dividend: 0,
                  }
                )

                if (!total_divisor || !total_dividend) {
                  return 0
                }

                return total_divisor && total_dividend
                  ? total_dividend / total_divisor
                  : 0
              },
            },
            {
              id: 'NEGATIVE_AR',
              type: 'MONEY',
              text: 'Negative AR',
            },
            {
              id: 'PATIENT_ACCOUNTS',
              type: 'NUMBER',
              text: 'Balance Due (Patients)',
            },
            {
              id: 'PATIENTS_30_PLUS',
              type: 'NUMBER',
              text: 'Pat 30+',
            },
            {
              id: 'PATS_30_PLUS_PERCENT',
              type: 'PERCENTAGE',
              text: '30+ Pat Ratio (%)',
              valueGetter: params =>
                generateNumberValueGetterFunc(
                  params,
                  'PATIENT_ACCOUNTS',
                  'PATIENTS_30_PLUS'
                ),
              aggFunc: params => generateNumberAggrFunc(params),
            },
            {
              id: 'PATIENTS_60_PLUS',
              type: 'NUMBER',
              text: 'Pat 60+',
            },
            {
              id: 'PATS_60_PLUS_PERCENT',
              type: 'PERCENTAGE',
              text: '60+ Pat Ratio (%)',
              valueGetter: params =>
                generateNumberValueGetterFunc(
                  params,
                  'PATIENT_ACCOUNTS',
                  'PATIENTS_60_PLUS'
                ),
              aggFunc: params => generateNumberAggrFunc(params),
            },
            {
              id: 'INSURANCE_ACCOUNTS',
              type: 'NUMBER',
              text: 'Balance Due (Insurance)',
            },
            {
              id: 'INS_30_PLUS',
              type: 'NUMBER',
              text: 'Ins 30+',
            },
            {
              id: 'INS_30_PLUS_PERCENT',
              type: 'PERCENTAGE',
              text: '30+ Ins Ratio (%)',
              valueGetter: params =>
                generateNumberValueGetterFunc(
                  params,
                  'INSURANCE_ACCOUNTS',
                  'INS_30_PLUS'
                ),
              aggFunc: params => generateNumberAggrFunc(params),
            },
            {
              id: 'INS_60_PLUS',
              type: 'NUMBER',
              text: 'Ins 60+',
            },
            {
              id: 'INS_60_PLUS_PERCENT',
              type: 'PERCENTAGE',
              text: '60+ Ins Ratio (%)',
              valueGetter: params =>
                generateNumberValueGetterFunc(
                  params,
                  'INSURANCE_ACCOUNTS',
                  'INS_60_PLUS'
                ),
              aggFunc: params => generateNumberAggrFunc(params),
            },
          ],
        },
        {
          id: 'STATS',
          type: 'GROUP',
          text: 'Stats',
          children: [
            {
              id: 'NEW_PAT_EXAMS',
              type: 'NUMBER',
              text: 'New Patient Exams Completed',
            },
            {
              id: 'CASE_STARTS',
              type: 'NUMBER',
              text: 'Contract Starts',
              headerTooltip:
                'This accounts for the number of financial contracts started in the given timeframe with the charge "Tx Fee"',
            },
            {
              id: 'PH_STARTS_PERCENT',
              type: 'PERCENTAGE',
              text: 'Starts to Exam Ratio (%)',
              valueGetter: params =>
                generateNumberValueGetterFunc(
                  params,
                  'NEW_PAT_EXAMS',
                  'CASE_STARTS'
                ),
              aggFunc: params => generateNumberAggrFunc(params),
            },
            {
              id: 'START_APPT_COMPLETED',
              type: 'NUMBER',
              text: 'Start Appt Completed',
              headerTooltip: `This figure counts each patient's first Start TX appointment per treatment, using the start appointment location where that appointment took place`,
            },
            {
              id: 'AVG_CASE_FEE',
              type: 'MONEY',
              text: 'Avg Case Fee',
              valueGetter: params =>
                generateNumberValueGetterFunc(
                  params,
                  'CASE_STARTS',
                  'GROSS_PRODUCTION',
                  false
                ),
              aggFunc: params => generateNumberAggrFunc(params, false),
            },
            {
              id: 'NEW_PATIENTS_CREATED',
              type: 'NUMBER',
              text: 'New Patients Created',
            },
            {
              id: 'ACTIVE_TX_PATIENTS',
              type: 'NUMBER',
              text: 'Active Patients',
            },
          ],
        },
      ],
      filter_values: {
        location_ids: [current_location.id],
        start_date: startOfMonth(new Date()),
        end_date: new Date(),
        ...last_execution.filters,
      },
      filters: [
        {
          id: 'START_DATE',
          column_name: 'Date',
          type: 'DATE_RANGE',
          required: true,
        },
        {
          id: 'LOCATION',
          column_name: 'Location',
          type: 'LOCATION',
          required: true,
        },
      ],
      last_execution,
      menu: true,
      report_type_values: ['PRODUCTION', 'AR', 'STATS'],
      report_types: [
        {
          id: 'PRODUCTION',
          text: 'Production',
        },
        {
          id: 'AR',
          text: 'A/R',
        },
        {
          id: 'STATS',
          text: 'Stats',
        },
      ],
    }
  },
  [REPORT_TYPES.PRODUCTION_BY_TRANSACTION_DATE]: ({
    last_execution,
    current_location,
    viewPath,
  }) => {
    return {
      type: REPORT_TYPES.PRODUCTION_BY_TRANSACTION_DATE,
      title: 'Production by transaction date',
      beta: true,
      columns: [
        {
          id: 'LOCATION',
          type: 'STRING',
          text: 'Location',
          context: {
            hide: false,
          },
        },
        {
          id: 'TRANSACTION_DATE',
          type: 'DATE',
          text: 'Transaction Date',
        },
        {
          id: 'REALIZATION_DATE',
          type: 'DATE',
          text: 'Realization Date',
        },
        {
          id: 'TRANSACTION_TYPE',
          type: 'STRING',
          text: 'Type',
        },
        {
          id: 'INVOICE',
          type: 'STRING',
          text: 'Invoice #',
        },
        {
          id: 'DETAILS',
          type: 'STRING',
          text: 'Details',
        },
        {
          id: 'GROSS_PRODUCTION',
          type: 'MONEY',
          text: 'Gross',
        },
        {
          id: 'PRODUCTION_DISCOUNT',
          type: 'MONEY',
          text: 'Discounts',
        },
        {
          id: 'PRODUCTION_ADJUSTMENT',
          type: 'MONEY',
          text: 'Adjustments',
        },
        {
          id: 'NET_PRODUCTION',
          type: 'MONEY',
          text: 'Net',
        },
        {
          id: 'ACCOUNT_TYPE',
          type: 'STRING',
          text: 'Account Type',
        },
        {
          id: 'PATIENT',
          type: 'STRING',
          text: 'Patient',
        },
        {
          id: 'PATIENT_NUMBER',
          type: 'STRING',
          text: 'Patient #',
        },
        {
          id: 'LEGACY_PATIENT_NUMBER',
          type: 'STRING',
          text: 'Legacy Patient #',
        },
        {
          id: 'PAYOR',
          type: 'STRING',
          text: 'Payor',
        },
        {
          id: 'TREATMENT_PLAN_NAME',
          type: 'STRING',
          text: 'Tx Plan Name',
        },
        {
          id: 'TREATMENT_LENGTH',
          type: 'NUMBER',
          text: 'Tx Length',
        },
        {
          id: 'TREATMENT_INSURANCE_CODE',
          type: 'STRING',
          text: 'Tx Insurance Code',
        },
        {
          id: 'TREATMENT_CATEGORY',
          type: 'STRING',
          text: 'Tx Category',
        },
        {
          id: 'NOTES',
          type: 'STRING',
          text: 'Notes',
        },
      ],
      filter_values: {
        location_ids: [current_location.id],
        start_date: subMonths(new Date(), 1),
        end_date: new Date(),
        financial_payor_types: [],
        ...last_execution.filters,
      },
      filters: [
        {
          id: 'START_DATE',
          column_name: 'Transaction Date',
          type: 'DATE_RANGE',
          required: true,
        },
        {
          id: 'LOCATION',
          column_name: 'Location',
          type: 'LOCATION',
          required: true,
        },
        {
          id: 'FINANCIAL_PAYOR_TYPE',
          column_name: 'Type',
          type: 'FINANCIAL_PAYOR_TYPE',
          required: true,
        },
      ],
      last_execution,
      menu: true,
    }
  },
  [REPORT_TYPES.STATEMENTS]: ({
    last_execution,
    current_location,
    viewPath,
  }) => {
    let today = new Date()
    let reference_date = isLastDayOfMonth(today)
      ? today
      : endOfMonth(subMonths(today, 1))

    return {
      type: REPORT_TYPES.STATEMENTS,
      title: 'Statements',
      beta: true,
      columns: [
        {
          headerCheckboxSelection: true,
          checkboxSelection: params => Boolean(params.data),
          id: 'PAYER_PERSON',
          text: 'Payor',
          type: 'STRING',
          valueGetter: params =>
            params.data
              ? `${params.data.PAYER_FIRST_NAME} ${params.data.PAYER_LAST_NAME}`
              : null,
        },
        {
          id: 'PATIENT_PERSON',
          text: 'Patient',
          type: 'PATIENT_LINK',
          valueGetter: params =>
            params.data
              ? `${params.data.PATIENT_FIRST_NAME} ${params.data.PATIENT_LAST_NAME}`
              : null,
          cellRenderer: params =>
            params.data ? (
              <PatientLinkCellRenderer
                viewPath={viewPath}
                value={params.valueFormatted || params.value}
                patient_id={params.data.PATIENT_ID}
              />
            ) : null,
        },
        {
          id: 'LOCATION_NAME',
          text: 'Location',
          type: 'STRING',
        },
        {
          id: 'DUE_AMOUNT',
          text: 'Due amount',
          type: 'MONEY',
        },
        {
          id: 'DUE_BUCKETS',
          text: 'Overdue',
          minWidth: 220,
          type: 'CUSTOM',
          cellRenderer: params =>
            params.data ? (
              <DueBuckets value={params.data} viewPath={viewPath} />
            ) : null,
        },
        {
          id: 'PDF',
          text: 'PDF',
          type: 'CUSTOM',
          cellRenderer: params =>
            params.data ? (
              <ViewPdfAction statement={params.data} viewPath={viewPath} />
            ) : null,
        },
      ],
      filter_values: {
        // TODO: check this, it's not respecting the report filters
        location_ids: [current_location._id],
        reference_date: formatISO(reference_date),
        transactions_start_date: formatISO(startOfMonth(reference_date)),
        transactions_end_date: formatISO(reference_date),
        min_amount: 0.01,
        due_buckets: last_execution.filters
          ? [
              last_execution.filters.include_due_0_30_days &&
                DUE_BUCKETS_TO_LABELS.DUE_0_30,
              last_execution.filters.include_due_31_60_days &&
                DUE_BUCKETS_TO_LABELS.DUE_31_60,
              last_execution.filters.include_due_61_90_days &&
                DUE_BUCKETS_TO_LABELS.DUE_61_90,
              last_execution.filters.include_due_91_120_days &&
                DUE_BUCKETS_TO_LABELS.DUE_91_120,
              last_execution.filters.include_due_more_120_days &&
                DUE_BUCKETS_TO_LABELS.DUE_MORE_120_DAYS,
            ].filter(Boolean)
          : DUE_BUCKETS_TO_LABELS_IN_ORDER,
        ...last_execution.filters,
      },
      // filters are handled separately for now in the financials section
      filters: [],
      last_execution,
      menu: false,
    }
  },
  [REPORT_TYPES.TOKEN_MIGRATION]: ({
    last_execution,
    current_location,
    viewPath,
  }) => {
    return {
      type: REPORT_TYPES.TOKEN_MIGRATION,
      title: 'Token Migration',
      beta: false,
      columns: [
        {
          id: 'REFERENCE_DATE',
          type: 'DATE',
          text: 'Reference Date',
        },
        {
          id: 'LOCATION',
          type: 'STRING',
          text: 'Location',
        },
        {
          id: 'PROVIDER',
          type: 'STRING',
          text: 'Provider',
        },
        {
          id: 'ACCOUNT_ID',
          type: 'STRING',
          text: 'Account ID',
        },
        {
          id: 'OLD_TOKEN',
          type: 'STRING',
          text: 'Old Token',
        },
        {
          id: 'METHOD',
          type: 'STRING',
          text: 'Method',
        },
        {
          id: 'HOLDER_NAME',
          type: 'STRING',
          text: 'Holder Name',
        },
        {
          id: 'MASKED_ACCOUNT',
          type: 'STRING',
          text: 'Masked Account',
        },
        {
          id: 'EXP_DATE',
          type: 'STRING',
          text: 'Exp Date',
        },
        {
          id: 'BRAND',
          type: 'STRING',
          text: 'Brand',
        },
        {
          id: 'CUSTOMER_PERSON_ID',
          type: 'STRING',
          text: 'Customer Person ID',
        },
        {
          id: 'CUSTOMER_EXTERNAL_ID',
          type: 'STRING',
          text: 'Customer External ID',
        },
        {
          id: 'COMMENTS',
          type: 'STRING',
          text: 'Comments',
        },
      ],
      filter_values: {
        location_ids: [current_location.id],
        reference_date: new Date(),
        ...last_execution.filters,
      },
      filters: [
        {
          id: 'REFERENCE_DATE',
          column_name: 'Reference Date',
          type: 'DATE',
          required: true,
        },
        {
          id: 'LOCATION',
          column_name: 'Location',
          type: 'LOCATION',
          required: true,
        },
      ],
      last_execution,
      menu: true,
    }
  },
  [REPORT_TYPES.TRANSACTIONS]: ({ last_execution, current_location }) => {
    let today = new Date()

    return {
      type: REPORT_TYPES.TRANSACTIONS,
      title: 'Transactions',
      beta: true,
      columns: [
        {
          id: 'LOCATION',
          type: 'STRING',
          text: 'Location',
        },
        {
          id: 'Effective Date',
          type: 'DATE_TIME',
          text: 'Effective Date',
        },
        {
          id: 'Realization Date',
          type: 'DATE',
          text: 'Realization Date',
        },
        {
          id: 'Invoice ID',
          type: 'STRING',
          text: 'Invoice ID',
        },
        {
          id: 'Transaction Type',
          type: 'STRING',
          text: 'Transaction Type',
        },
        {
          id: 'Details',
          type: 'STRING',
          text: 'Details',
        },
        {
          id: 'Description',
          type: 'STRING',
          text: 'Description',
        },
        {
          id: 'Production',
          type: 'STRING',
          text: 'Production',
        },
        {
          id: 'Due Date',
          type: 'DATE',
          text: 'Due Date',
        },
        {
          id: 'Root Invoice Date',
          type: 'DATE',
          text: 'Root Invoice Date',
        },
        {
          id: 'Amount',
          type: 'MONEY',
          text: 'Amount',
        },
        {
          id: 'Applied Amount',
          type: 'MONEY',
          text: 'Applied Amount',
        },
        {
          id: 'Declined Amount',
          type: 'MONEY',
          text: 'Declined Amount',
        },
        {
          id: 'Patient ID',
          type: 'HUMAN_ID',
          text: 'Patient ID',
        },
        {
          id: 'Patient Name',
          type: 'STRING',
          text: 'Patient Name',
        },
        {
          id: 'Payer Name',
          type: 'STRING',
          text: 'Payor Name',
        },
        {
          id: 'Payer Type',
          type: 'STRING',
          text: 'Payor Type',
        },
        {
          id: 'Tx Start Date',
          type: 'DATE',
          text: 'Tx Start Date',
        },
        {
          id: 'Tx End Date',
          type: 'DATE',
          text: 'Tx End Date',
        },
        {
          id: 'Payment Location',
          type: 'STRING',
          text: 'Payment Location',
        },
        {
          id: 'Tx Location',
          type: 'STRING',
          text: 'Tx Location',
        },
        {
          id: 'Invoice Location',
          type: 'STRING',
          text: 'Invoice Location',
        },
        {
          id: 'Tx Provider Name',
          type: 'STRING',
          text: 'Tx Provider Name',
        },
        {
          id: 'Tx Plan Name',
          type: 'STRING',
          text: 'Tx Plan Name',
        },
        {
          id: 'Tx Plan Length',
          type: 'STRING',
          text: 'Tx Plan Length',
        },
        {
          id: 'Payment Plan Length',
          type: 'STRING',
          text: 'Payment Plan Length',
        },
        {
          id: 'Payment Plan First Due Date',
          type: 'DATE',
          text: 'Payment Plan First Due Date',
        },
        {
          id: 'Payment Plan First Installment Date',
          type: 'DATE',
          text: 'Payment Plan First Installment Date',
        },
        {
          id: 'Payment Plan Installment Interval',
          type: 'STRING',
          text: 'Payment Plan Installment Interval',
        },
        {
          id: 'Payment Plan Instalment Amount',
          type: 'MONEY',
          text: 'Payment Plan Instalment Amount',
        },
        {
          id: 'Payment Plan Second Installment Date',
          type: 'DATE',
          text: 'Payment Plan Second Installment Date',
        },
        {
          id: 'Payment Plan Type',
          type: 'STRING',
          text: 'Payment Plan Type',
        },
        {
          id: 'Payment Name',
          type: 'STRING',
          text: 'Payment Name',
        },
        {
          id: 'Payment Reference ID',
          type: 'STRING',
          text: 'Payment Reference ID',
        },
        {
          id: 'Payment Order ID',
          type: 'STRING',
          text: 'Payment Order ID',
        },
        {
          id: 'Collection Label Name',
          type: 'STRING',
          text: 'Collection Label Name',
        },
        {
          id: 'Collection Label Agency',
          type: 'STRING',
          text: 'Collection Label Agency',
        },
      ],
      filter_values: {
        location_ids: [current_location.id],
        start_date: formatISO(startOfMonth(today)),
        end_date: formatISO(today),
        // yes, they aren't IDs
        transaction_type_ids: [
          'Applied Payment',
          'Charge Transfer',
          'Corrections',
          'Discount Reverse',
          'Discount',
          'Fix Adjustment',
          'Installment Charge',
          'Late Fee',
          'Migration Fix',
          'MiscFee Charge',
          'Payment Plan Rollback',
          'Payment Plan',
          'Payment',
          'Refund',
          'Retail Fee',
          'Transfer Charge',
          'TxFee Charge',
          'Unapplied Payment',
          'Write Off',
        ],
        ...last_execution.filters,
      },
      filters: [
        {
          id: 'TRANSACTION_TYPE',
          column_name: 'Transaction Type',
          type: 'TRANSACTION_TYPE',
          required: true,
        },
        {
          id: 'START_DATE',
          column_name: 'Date range',
          type: 'DATE_RANGE',
          required: true,
        },
        {
          id: 'LOCATION',
          column_name: 'Locations',
          type: 'LOCATION',
          required: true,
        },
      ],
      last_execution,
      menu: true,
    }
  },
  [REPORT_TYPES.UNAPPLIED_PAYMENTS]: ({
    last_execution,
    current_location,
    viewPath,
  }) => {
    return {
      type: REPORT_TYPES.UNAPPLIED_PAYMENTS,
      title: 'Unapplied Payments',
      beta: false,
      columns: [
        {
          id: 'LOCATION',
          type: 'STRING',
          text: 'Location',
          context: {
            hide: false,
          },
        },
        { id: 'PATIENT_ID', type: 'HUMAN_ID', text: 'Patient ID' },
        { id: 'PATIENT_NAME', type: 'STRING', text: 'Patient Name' },
        { id: 'PAYER_NAME', type: 'STRING', text: 'Payor Name' },
        { id: 'PAYER_TYPE', type: 'STRING', text: 'Payor Type' },
        { id: 'PAYMENT_ID', type: 'STRING', text: 'Payment ID' },
        { id: 'PAYMENT_DATE', type: 'DATE', text: 'Payment Date' },
        { id: 'PAYMENT_TYPE', type: 'STRING', text: 'Payment Type' },
        { id: 'PAYMENT_NOTES', type: 'STRING', text: 'Details' },
        { id: 'PAYMENT_BALANCE', type: 'MONEY', text: 'Unapplied Amount' },
        {
          id: 'PAYMENT_INITIAL_AMOUNT',
          type: 'MONEY',
          text: 'Initial Amount',
        },
        {
          id: 'PATIENT_ACCOUNT_BALANCE',
          type: 'MONEY',
          text: 'Account Balance',
        },
      ],
      filter_values: {
        location_ids: [current_location.id],
        start_date: startOfMonth(new Date()),
        end_date: new Date(),
        ...last_execution.filters,
      },
      filters: [
        {
          id: 'START_DATE',
          column_name: 'Date',
          type: 'DATE_RANGE',
          required: true,
        },
        {
          id: 'LOCATION',
          column_name: 'Location',
          type: 'LOCATION',
          required: true,
        },
      ],
      last_execution,
      menu: true,
      report_type_values: [],
      report_types: [],
    }
  },
})

function PatientCellRenderer(props) {
  let submit = useDataSubmit({
    context: 'tab',
    viewPath: props.viewPath,
  })

  if (!props.value) return null

  let data = props.data || null
  if (data === null && props.node.group) {
    data =
      props.node.allLeafChildren.find(item => typeof item.data === 'object')
        ?.data || null
  }

  return (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      <TysiaButton
        viewPath={`${props.viewPath}/TysiaButton`}
        onClick={onClick}
        size="small"
        type="ghost"
        marginTop={0}
        marginRight={4}
        title="Profile"
        alignSelf="auto"
      >
        {childProps => (
          <TysiaButtonIcon
            {...childProps}
            viewPath={`${props.viewPath}/TysiaButtonIcon`}
          >
            {childProps => (
              <TysiaIconPerson
                {...childProps}
                viewPath={`${props.viewPath}/TysiaIconPerson`}
              />
            )}
          </TysiaButtonIcon>
        )}
      </TysiaButton>
      {props.valueFormatted || props.value}
    </span>
  )

  async function onClick(event) {
    event.stopPropagation()
    submit({
      type: 'open_patient_tab',
      patient_id: data.PATIENT_INTERNAL_ID || data.PATIENT_ID,
      patient_name: data.PATIENT_NAME || data.PATIENT_FULL_NAME,
      tab_name: props.tabName,
    })
  }
}

function PatientLinkCellRenderer(props) {
  let submit = useDataSubmit({
    context: 'global',
    viewPath: props.viewPath,
  })

  return (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      <TysiaButton
        viewPath={`${props.viewPath}/TysiaButton`}
        onClick={onClick}
        size="small"
        type="ghost"
        marginTop={0}
        marginRight={4}
        title="Ledger"
        alignSelf="auto"
      >
        {childProps => (
          <TysiaButtonIcon
            {...childProps}
            viewPath={`${props.viewPath}/TysiaButtonIcon`}
          >
            {childProps => (
              <TysiaIconPerson
                {...childProps}
                viewPath={`${props.viewPath}/TysiaIconPerson`}
              />
            )}
          </TysiaButtonIcon>
        )}
      </TysiaButton>
      {props.value}
    </span>
  )

  function onClick(event) {
    event.stopPropagation()
    submit({
      type: 'tabs/add',
      focus: false,
      tab: {
        id: props.patient_id,
        type: 'Patient',
        name: props.value,
        patient_id: props.patient_id,
        tab: 'ledger',
        viewPath: '/App/Account/Content/Patients',
      },
    })
  }
}

function ViewPdfAction(props) {
  let submit = useDataSubmit({
    context: 'selected',
    viewPath: props.viewPath,
  })

  return (
    <TysiaButton
      viewPath={props.viewPath}
      type="ghost"
      size="small"
      onClick={event => {
        event.stopPropagation()
        submit({ type: 'statements/view', statement: props.statement })
      }}
    >
      {childProps => (
        <TysiaButtonText
          {...childProps}
          text="View"
          viewPath={props.viewPath}
        />
      )}
    </TysiaButton>
  )
}

function DueBuckets(props) {
  let buckets = [
    props.value.DUE_0_30_DAYS > 0 && DUE_BUCKETS_TO_LABELS.DUE_0_30,
    props.value.DUE_31_60_DAYS > 0 && DUE_BUCKETS_TO_LABELS.DUE_31_60,
    props.value.DUE_61_90_DAYS > 0 && DUE_BUCKETS_TO_LABELS.DUE_61_90,
    props.value.DUE_91_120_DAYS > 0 && DUE_BUCKETS_TO_LABELS.DUE_91_120,
    props.value.DUE_MORE_120_DAYS > 0 &&
      DUE_BUCKETS_TO_LABELS.DUE_MORE_120_DAYS,
  ].filter(Boolean)

  return (
    <div
      className="views-block"
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        height: '100%',
        gap: 2,
      }}
    >
      {buckets.map(item => (
        <TysiaTag
          key={item}
          alignSelf="center"
          viewPath={`${props.viewPath}/TysiaTag`}
          type="grey"
          style="outline"
        >
          {childProps => (
            <TysiaTagText
              {...childProps}
              viewPath={`${props.viewPath}/TysiaTagText`}
              text={item}
            />
          )}
        </TysiaTag>
      ))}
    </div>
  )
}

export let DUE_BUCKETS = /** @type {const} */ ({
  DUE_0_30: 'DUE_0_30',
  DUE_31_60: 'DUE_31_60',
  DUE_61_90: 'DUE_61_90',
  DUE_91_120: 'DUE_91_120',
  DUE_MORE_120_DAYS: 'DUE_MORE_120_DAYS',
})
export let DUE_BUCKETS_TO_LABELS = /** @type {const} */ ({
  [DUE_BUCKETS.DUE_0_30]: '0-30',
  [DUE_BUCKETS.DUE_31_60]: '31-60',
  [DUE_BUCKETS.DUE_61_90]: '61-90',
  [DUE_BUCKETS.DUE_91_120]: '91-120',
  [DUE_BUCKETS.DUE_MORE_120_DAYS]: '120+',
})
export let DUE_BUCKETS_IN_ORDER = /** @type {const} */ ([
  DUE_BUCKETS.DUE_0_30,
  DUE_BUCKETS.DUE_31_60,
  DUE_BUCKETS.DUE_61_90,
  DUE_BUCKETS.DUE_91_120,
  DUE_BUCKETS.DUE_MORE_120_DAYS,
])
export let DUE_BUCKETS_TO_LABELS_IN_ORDER = /** @type {const} */ (
  DUE_BUCKETS_IN_ORDER.map(item => DUE_BUCKETS_TO_LABELS[item])
)
